import { useState, useCallback, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet-distortableimage';
import 'leaflet-distortableimage/dist/leaflet.distortableimage.css';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

// Disable all keyboard shortcuts from leaflet-distortableimage — they conflict
// with sidebar text inputs and cause accidental mode changes (e.g. 'd' for drag).
if (L.DistortableImage) L.DistortableImage.action_map = {};

/**
 * Manages plan overlay images (PNG/JPG) as distortable Leaflet layers.
 *
 * Overlays are stored on the takeoff document (takeoff.overlays). This hook:
 * - Loads overlays from the takeoff and renders them on the map
 * - Lets the user upload new images (content-hash deduplicated server-side)
 * - Manages opacity, visibility, locking, category/subType
 * - Tracks an "active" overlay — only the active one is draggable, to
 *   prevent accidental interaction with locked overlays underneath
 * - Returns an array of overlay objects that can be saved back to the takeoff
 *
 * Overlay shape:
 *   {
 *     id: string (client-generated uuid, used as a key),
 *     imageUrl: string,
 *     corners: [{ lat, lng }, { lat, lng }, { lat, lng }, { lat, lng }],
 *     opacity: number (0-1),
 *     visible: boolean,
 *     locked: boolean,
 *     category: 'aerial' | 'drawing' | 'plans' | '',
 *     subType: string,
 *   }
 */
export default function useOverlays({ url, mapRef, jobsite, initialOverlays }) {
  const [overlays, setOverlays] = useState([]);
  const [activeOverlayId, setActiveOverlayId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [savedOverlayUrls, setSavedOverlayUrls] = useState([]); // previously uploaded for this jobsite
  const layersRef = useRef({}); // overlayId -> distortableImageOverlay
  const initializedRef = useRef(false);

  // Restore overlays from the loaded takeoff
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Clear existing layers
    Object.values(layersRef.current).forEach(layer => {
      try { layer.remove(); } catch (_) {}
    });
    layersRef.current = {};

    if (!Array.isArray(initialOverlays) || initialOverlays.length === 0) {
      setOverlays([]);
      setActiveOverlayId(null);
      return;
    }

    // Defer a tick to make sure the map's marker pane exists
    const doRestore = () => {
      const restored = [];
      initialOverlays.forEach((ov) => {
        if (!ov?.imageUrl || !ov?.corners?.length || ov.corners.length !== 4) return;
        const id = uuidv4();
        const corners = ov.corners.map(c => L.latLng(c.lat, c.lng));
        const opacity = ov.opacity ?? 0.6;
        const isLocked = !!ov.locked;

        try {
          const layer = L.distortableImageOverlay(ov.imageUrl, {
            corners,
            mode: isLocked ? 'lock' : 'rotate',
            selected: false,
            opacity,
          }).addTo(map);

          layersRef.current[id] = layer;
          restored.push({
            id,
            imageUrl: ov.imageUrl,
            visible: true,
            opacity,
            locked: isLocked,
            category: ov.category || '',
            subType: ov.subType || '',
          });
        } catch (err) {
          console.warn('Failed to restore overlay:', err);
        }
      });
      setOverlays(restored);
      initializedRef.current = true;
    };

    if (map.getPane('markerPane')) {
      doRestore();
    } else {
      map.whenReady(doRestore);
    }

    return () => {
      Object.values(layersRef.current).forEach(layer => {
        try { layer.remove(); } catch (_) {}
      });
      layersRef.current = {};
    };
  }, [initialOverlays, mapRef]);

  // Sync pointer events and editing state based on activeOverlayId + locked
  useEffect(() => {
    overlays.forEach(ov => {
      const layer = layersRef.current[ov.id];
      if (!layer) return;

      // Interactive only if: visible, active, AND not locked
      const shouldBeInteractive = ov.visible && ov.id === activeOverlayId && !ov.locked;
      const el = layer.getElement();
      const container = layer._image?.parentElement;

      if (shouldBeInteractive) {
        if (layer.editing && !layer.editing.enabled()) {
          try { layer.editing.enable(); } catch (_) {}
        }
        if (el) {
          el.style.pointerEvents = '';
          el.style.zIndex = '650';
        }
        if (container) container.style.pointerEvents = '';
      } else {
        if (layer.editing && layer.editing.enabled()) {
          try {
            layer.deselect();
            layer.editing.disable();
          } catch (_) {}
        }
        if (el) {
          el.style.pointerEvents = 'none';
          el.style.zIndex = '';
        }
        if (container) container.style.pointerEvents = 'none';
      }
    });
  }, [activeOverlayId, overlays]);

  // Fetch previously-saved overlay URLs for this jobsite (for the "re-use" picker)
  useEffect(() => {
    if (!url || !jobsite?._id) {
      setSavedOverlayUrls([]);
      return;
    }
    axios
      .get(`${url}/api/customer/site-takeoffs?jobSiteId=${jobsite._id}`)
      .then(resp => {
        setSavedOverlayUrls(resp.data?.overlayUrls || []);
      })
      .catch(() => setSavedOverlayUrls([]));
  }, [url, jobsite]);

  // ===== Upload a new overlay image =====
  const uploadOverlay = useCallback(async (file) => {
    if (!url || !jobsite?._id || !file) return null;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('jobSiteId', jobsite._id);
      const resp = await axios.post(
        `${url}/api/customer/site-takeoff-overlay-upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (resp.data?.success && resp.data?.objectUrl) {
        const objectUrl = resp.data.objectUrl;
        setSavedOverlayUrls(prev => prev.includes(objectUrl) ? prev : [...prev, objectUrl]);
        addOverlayToMap(objectUrl);
        return objectUrl;
      }
    } catch (err) {
      console.error('uploadOverlay error:', err);
    } finally {
      setUploading(false);
    }
    return null;
  }, [url, jobsite]);

  // Add an overlay to the map centered on the current view
  const addOverlayToMap = useCallback((imgUrl) => {
    const map = mapRef.current;
    if (!map || !imgUrl) return;

    const id = uuidv4();
    const center = map.getCenter();
    const bounds = map.getBounds();
    const latSpan = (bounds.getNorth() - bounds.getSouth()) * 0.25;
    const lngSpan = (bounds.getEast() - bounds.getWest()) * 0.25;

    const corners = [
      L.latLng(center.lat + latSpan, center.lng - lngSpan),
      L.latLng(center.lat + latSpan, center.lng + lngSpan),
      L.latLng(center.lat - latSpan, center.lng - lngSpan),
      L.latLng(center.lat - latSpan, center.lng + lngSpan),
    ];

    try {
      const layer = L.distortableImageOverlay(imgUrl, {
        corners,
        mode: 'rotate',
        selected: false,
        opacity: 0.6,
      }).addTo(map);

      layersRef.current[id] = layer;
      setOverlays(prev => [
        ...prev,
        {
          id,
          imageUrl: imgUrl,
          visible: true,
          opacity: 0.6,
          locked: false,
          category: '',
          subType: '',
        },
      ]);
      // Auto-activate the new overlay so the user can start aligning it
      setActiveOverlayId(id);
    } catch (err) {
      console.error('addOverlayToMap error:', err);
    }
  }, [mapRef]);

  // ===== Mutations =====
  const setOverlayVisible = useCallback((ovId, visible) => {
    const layer = layersRef.current[ovId];
    if (layer) {
      if (visible) {
        setOverlays(prev => {
          const ov = prev.find(o => o.id === ovId);
          if (ov) layer.setOpacity(ov.opacity);
          return prev;
        });
      } else {
        try { layer.deselect(); } catch (_) {}
        layer.setOpacity(0);
      }
    }
    setOverlays(prev => prev.map(o => o.id === ovId ? { ...o, visible } : o));
    if (!visible && activeOverlayId === ovId) setActiveOverlayId(null);
  }, [activeOverlayId]);

  const setOverlayOpacity = useCallback((ovId, opacity) => {
    const layer = layersRef.current[ovId];
    if (layer) layer.setOpacity(opacity);
    setOverlays(prev => prev.map(o => o.id === ovId ? { ...o, opacity } : o));
  }, []);

  const setMasterOpacity = useCallback((opacity) => {
    setOverlays(prev => prev.map(o => {
      const layer = layersRef.current[o.id];
      if (layer && o.visible) layer.setOpacity(opacity);
      return { ...o, opacity };
    }));
  }, []);

  const setOverlayLocked = useCallback((ovId, locked) => {
    const layer = layersRef.current[ovId];
    if (layer && layer.editing) {
      try {
        if (locked) layer.editing._lock();
        else layer.editing._unlock();
      } catch (_) {}
    }
    setOverlays(prev => prev.map(o => o.id === ovId ? { ...o, locked } : o));
    // Unlocking activates the overlay so it becomes immediately editable;
    // Locking deactivates it so pointer events get removed
    if (locked) {
      setActiveOverlayId(prev => prev === ovId ? null : prev);
    } else {
      setActiveOverlayId(ovId);
    }
  }, []);

  const setOverlayCategory = useCallback((ovId, field, value) => {
    setOverlays(prev => prev.map(o => {
      if (o.id !== ovId) return o;
      if (field === 'category') return { ...o, category: value, subType: '' };
      return { ...o, [field]: value };
    }));
  }, []);

  const activateOverlay = useCallback((ovId) => {
    setActiveOverlayId(prev => prev === ovId ? null : ovId);
  }, []);

  const removeOverlay = useCallback((ovId) => {
    const layer = layersRef.current[ovId];
    if (layer) {
      try { layer.deselect(); layer.remove(); } catch (_) {}
      delete layersRef.current[ovId];
    }
    setOverlays(prev => prev.filter(o => o.id !== ovId));
    if (activeOverlayId === ovId) setActiveOverlayId(null);
  }, [activeOverlayId]);

  // Add an existing (previously uploaded) overlay URL to the map
  const addExistingOverlay = useCallback((imgUrl) => {
    addOverlayToMap(imgUrl);
  }, [addOverlayToMap]);

  // ===== Serialize current overlays for saving back to the takeoff =====
  // Reads corners from the live layers so any drag changes are captured.
  const serializeForSave = useCallback(() => {
    return overlays
      .map(ov => {
        const layer = layersRef.current[ov.id];
        if (!layer) return null;
        try {
          const corners = layer.getCorners();
          const imgUrl = layer._url || layer.getElement()?.src;
          if (corners?.length === 4 && imgUrl) {
            return {
              imageUrl: imgUrl,
              corners: corners.map(c => ({ lat: c.lat, lng: c.lng })),
              opacity: ov.opacity ?? 0.6,
              locked: !!ov.locked,
              category: ov.category || '',
              subType: ov.subType || '',
            };
          }
        } catch (e) {
          console.warn('Failed to serialize overlay:', e);
        }
        return null;
      })
      .filter(Boolean);
  }, [overlays]);

  return {
    overlays,
    activeOverlayId,
    uploading,
    savedOverlayUrls,
    uploadOverlay,
    addExistingOverlay,
    setOverlayVisible,
    setOverlayOpacity,
    setMasterOpacity,
    setOverlayLocked,
    setOverlayCategory,
    activateOverlay,
    removeOverlay,
    serializeForSave,
  };
}
